//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 07/04/2025
// Version       : 1
// Description   : Ver detalle de la carga de combustible de una patente
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import { useState, useEffect, useMemo } from "react";
import { UrlApi } from "../../services/apirest";
import { MaterialReactTable } from "material-react-table";
// TABLA EN ESPAÑOL
import { MRT_Localization_ES } from "material-react-table/locales/es";
import {
  AppBar,
  Box,
  Stack,
  Toolbar,
  Button,
  FormControlLabel,
  Checkbox,
  ThemeProvider,
  createTheme,
  useTheme,
} from "@mui/material";

// DatePicker
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { esES } from "@mui/x-date-pickers/locales";
import dayjs from "dayjs";
import "dayjs/locale/es";

// ICONOS
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

function DetallesPatente({
  patenteSeleccionada,
  handleCloseDetallesPatente,
  fechaDesdeReporte,
  fechaHastaReporte,
}) {
  const globalTheme = useTheme();
  // Estado para incluir/excluir productos inactivos
  const [showInactivos, setShowInactivos] = useState(false);
  const [initialShowInactivos, setInitialShowInactivos] = useState(false);

  //*************************************************************************************************************************************/

  const [fechaDesde, setFechaDesde] = useState(fechaDesdeReporte);
  const [fechaHasta, setFechaHasta] = useState(fechaHastaReporte);
  const [data, setData] = useState([]); // Datos de la tabla

  //*************************************************************************************************************************************/

  // Obtener datos
  const obtenerDatosPatente = () => {
    if (!patenteSeleccionada) return;

    let ruta = `/patente/${patenteSeleccionada.patente}`;
    // Para ver los inactivos se llama al sub-endpoint /activos
    if (!showInactivos) ruta += "/activos";

    let url = `${UrlApi}/vehiculos_reporte_combustibles${ruta}`;

    const formatoFecha = (f) => (f ? dayjs(f).format("YYYY-MM-DD") : null);
    const desde = formatoFecha(fechaDesde);
    const hasta = formatoFecha(fechaHasta);

    if (desde && hasta) {
      url += `?desde=${desde}&hasta=${hasta}`;
    }

    fetch(url)
      .then((res) => res.json())
      .then((rows) => {
        setData(rows);
        // Al completar con éxito, actualizo los "iniciales"
        setInitialFechaDesde(fechaDesde);
        setInitialFechaHasta(fechaHasta);
        setInitialShowInactivos(showInactivos);
      })
      .catch((err) => console.error("Error al obtener datos:", err));
  };

  useEffect(() => {
    obtenerDatosPatente();
  }, []);

  //*************************************************************************************************************************************/

  // ACTIVAR/DESACTIVAR BOTÓN DE FILTRAR
  const [isFilterEnabled, setIsFilterEnabled] = useState(false);
  const [initialFechaDesde, setInitialFechaDesde] = useState(null);
  const [initialFechaHasta, setInitialFechaHasta] = useState(null);

  useEffect(() => {
    const fechasSonValidas = fechaDesde && fechaHasta;
    const fechasCambiaron =
      fechaDesde !== initialFechaDesde || fechaHasta !== initialFechaHasta;
    setIsFilterEnabled(fechasSonValidas && fechasCambiaron);
  }, [fechaDesde, fechaHasta, initialFechaDesde, initialFechaHasta]);

  // useEffect que reacciona a cambios en fechas o en el checkbox
  useEffect(() => {
    // Validación de las fechas
    const ambasVacias = !fechaDesde && !fechaHasta;
    const ambasConValor = !!fechaDesde && !!fechaHasta;
    const fechasValidas = ambasVacias || ambasConValor;

    // Detecta si las fechas cambiaron
    const fechasCambiaron =
      fechaDesde !== initialFechaDesde || fechaHasta !== initialFechaHasta;

    // Detecta si el checkbox cambió
    const showCambiado = showInactivos !== initialShowInactivos;

    // Habilita el botón si hubo cambio en fachas y/o el checkbox
    setIsFilterEnabled(fechasValidas && (fechasCambiaron || showCambiado));
  }, [
    fechaDesde,
    fechaHasta,
    initialFechaDesde,
    initialFechaHasta,
    showInactivos,
    initialShowInactivos,
  ]);

  //*************************************************************************************************************************************/

  // ----------- TABLA -----------
  const columns = [
    {
      id: "fecha", // IMPORTANTE: usar `id` en vez de accessorKey
      header: "Fecha",
      size: 120,
      // accessorFn entrega un "Date" para grouping/sorting
      accessorFn: (row) => {
        // row.fecha === '2025-02-28'
        const [yy, mm, dd] = row.fecha.split("-");
        return new Date(`${yy}-${mm}-${dd}`);
      },
      // Función de ordenamiento de fechas
      sortingFn: "datetime",
      // Lo que se muestra en la celda
      Cell: ({ cell }) => {
        const dt = cell.getValue();
        if (!(dt instanceof Date)) return "";
        const d = dt.getDate().toString().padStart(2, "0");
        const m = (dt.getMonth() + 1).toString().padStart(2, "0");
        const y = dt.getFullYear();
        return `${d}-${m}-${y}`;
      },
    },
    {
      accessorKey: "hora",
      header: "Hora",
      size: 30,
    },
    {
      accessorKey: "producto",
      header: "Producto",
      size: 160,
      Cell: ({ cell, row }) => {
        const valor = cell.getValue(); // Nombre del producto
        const estado = row.original.estado; // 1 = activo, 2 = inactivo
        const proveedor = row.original.proveedor;

        // Se muestra nombre si hay valor y está activo
        if (valor && estado === 1) {
          return valor;
        }

        if (valor && estado !== 1) {
          return `🛑 INACTIVO (${valor})`; // Producto existe pero está inactivo
        }

        // Producto no cargado: se muestra proveedor si existe
        return `⚠️ AGREGAR PRODUCTO ⚠️${
          proveedor ? ` (Proveedor: ${proveedor})` : ""
        }`;
      },
    },
    {
      accessorKey: "cantidad",
      header: "Cantidad",
      size: 100,
      // Agregación (sum) para las celdas
      // Celda con cantidad + unidad
      Cell: ({ cell, row }) => {
        const valor = cell.getValue() ?? 0;
        // formato numérico
        const formatted = Number.isInteger(valor)
          ? valor.toLocaleString("es-AR")
          : valor.toLocaleString("es-AR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });

        const unidad = row.original.unidad ?? "";

        return (
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", pr: 2 }}
          >
            {/* Cantidad siempre visible */}
            <span style={{ marginRight: unidad ? 4 : 0 }}>{formatted}</span>
            {/* Unidad sólo si está activo */}
            {unidad && <b>{unidad}</b>}
          </Box>
        );
      },
      // Celda agrupada: sum + unidad de la primera fila del grupo
      AggregatedCell: ({ cell }) => {
        const valorAgrupado = cell.getValue() ?? 0;
        const formattedAgrupado = Number.isInteger(valorAgrupado)
          ? valorAgrupado.toLocaleString("es-AR")
          : valorAgrupado.toLocaleString("es-AR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
        return (
          <Box sx={{ fontWeight: "bold", marginRight: "72px" }}>
            {formattedAgrupado}
          </Box>
        );
      },
      // Footer con el total de la columna
      // getFilteredRowModel() para sumar solo lo que está filtrado en la tabla
      Footer: ({ table }) => {
        const totalCantidad = table
          .getFilteredRowModel()
          .rows.reduce((acc, row) => acc + (row.original?.cantidad || 0), 0);

        return (
          <Box sx={{ fontWeight: "bold", marginRight: "32px" }}>
            TOTAL:{" "}
            {Number.isInteger(totalCantidad)
              ? totalCantidad.toLocaleString("es-AR")
              : totalCantidad.toLocaleString("es-AR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
          </Box>
        );
      },
      muiTableBodyCellProps: {
        align: "right",
      },
      muiTableFooterCellProps: {
        align: "right",
      },
    },
    {
      accessorKey: "proveedor",
      header: "Proveedor",
      size: 80,
    },
    {
      accessorKey: "nro_comprobante",
      header: "Comprobante",
      size: 100,
    },
  ];

  const customLocalization = {
    ...MRT_Localization_ES,
    body: {
      ...MRT_Localization_ES.body,
      noData: "No hay registros para mostrar",
    },
  };

  const initialState = {
    density: "compact",
    showGlobalFilter: true,
    pagination: { pageIndex: 0, pageSize: 10 },
    grouping: ["fecha", "producto"],
    sorting: [
      { id: "fecha", desc: true },
      { id: "hora", desc: false },
    ],
  };

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 16,
      color: "white",
      fontWeight: "normal",
      backgroundColor: "#2471A3",
      borderColor: "black",
      "& .MuiSvgIcon-root": {
        color: "white", // color de los íconos
      },
    },
  };

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { backgroundColor: "#ffffff" },
    variant: "outlined",
    size: "small",
  };

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: globalTheme.palette.mode,
          primary: globalTheme.palette.primary,
          info: {
            main: "rgb(255,122,0)",
          },
          background: {
            default:
              globalTheme.palette.mode === "light"
                ? "rgb(234, 239, 241)"
                : "#000",
          },
        },
        typography: {
          button: {
            textTransform: "none",
            fontSize: "1rem",
          },
        },
        components: {
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: "0.8rem",
              },
            },
          },
        },
      }),
    [globalTheme]
  );

  return (
    <>
      <AppBar
        sx={{
          position: "relative",
        }}
      >
        <Toolbar sx={{ alignItems: "center" }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleCloseDetallesPatente}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          <Stack
            direction="row"
            spacing={1}
            sx={{ ml: 2, flex: 1, flexWrap: "wrap", alignItems: "center" }}
          >
            <Box
              sx={{
                display: "inline-flex",
                border: "1px solid #0d47a1",
                borderRadius: "16px",
                overflow: "hidden",
                fontWeight: "bold",
                fontSize: "0.875rem", // similar a un Chip
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#0d47a1",
                  color: "white",
                  padding: "4px 8px",
                  whiteSpace: "nowrap",
                  borderTopLeftRadius: "16px",
                  borderBottomLeftRadius: "16px",
                }}
              >
                Patente
              </Box>
              <Box
                sx={{
                  backgroundColor: "white",
                  color: "#0d47a1",
                  paddingTop: "4px",
                  paddingLeft: "6px",
                  paddingRight: "12px",
                  whiteSpace: "nowrap",
                  borderTopRightRadius: "16px",
                  borderBottomRightRadius: "16px",
                }}
              >
                {patenteSeleccionada.patente ?? "No disponible"}
              </Box>
            </Box>

            <Box
              sx={{
                display: "inline-flex",
                border: "1px solid #0d47a1",
                borderRadius: "16px",
                overflow: "hidden",
                fontWeight: "bold",
                fontSize: "0.875rem", // similar a un Chip
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#0d47a1",
                  color: "white",
                  padding: "4px 8px",
                  whiteSpace: "nowrap",
                  borderTopLeftRadius: "16px",
                  borderBottomLeftRadius: "16px",
                }}
              >
                Marca
              </Box>
              <Box
                sx={{
                  backgroundColor: "white",
                  color: "#0d47a1",
                  paddingTop: "4px",
                  paddingLeft: "6px",
                  paddingRight: "12px",
                  whiteSpace: "nowrap",
                  borderTopRightRadius: "16px",
                  borderBottomRightRadius: "16px",
                }}
              >
                {patenteSeleccionada?.marca ?? "No disponible"}
              </Box>
            </Box>

            <Box
              sx={{
                display: "inline-flex",
                border: "1px solid #0d47a1",
                borderRadius: "16px",
                overflow: "hidden",
                fontWeight: "bold",
                fontSize: "0.875rem",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#0d47a1",
                  color: "white",
                  padding: "4px 8px",
                  whiteSpace: "nowrap",
                  borderTopLeftRadius: "16px",
                  borderBottomLeftRadius: "16px",
                }}
              >
                Modelo
              </Box>
              <Box
                sx={{
                  backgroundColor: "white",
                  color: "#0d47a1",
                  paddingTop: "4px",
                  paddingLeft: "6px",
                  paddingRight: "12px",
                  whiteSpace: "nowrap",
                  borderTopRightRadius: "16px",
                  borderBottomRightRadius: "16px",
                }}
              >
                {patenteSeleccionada?.modelo ?? "No disponible"}
              </Box>
            </Box>

            <Box
              sx={{
                display: "inline-flex",
                border: "1px solid #0d47a1",
                borderRadius: "16px",
                overflow: "hidden",
                fontWeight: "bold",
                fontSize: "0.875rem",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#0d47a1",
                  color: "white",
                  padding: "4px 8px",
                  whiteSpace: "nowrap",
                  borderTopLeftRadius: "16px",
                  borderBottomLeftRadius: "16px",
                }}
              >
                Sucursal
              </Box>
              <Box
                sx={{
                  backgroundColor: "white",
                  color: "#0d47a1",
                  paddingTop: "4px",
                  paddingLeft: "6px",
                  paddingRight: "12px",
                  whiteSpace: "nowrap",
                  borderTopRightRadius: "16px",
                  borderBottomRightRadius: "16px",
                }}
              >
                {patenteSeleccionada?.sucursal ?? "No disponible"}
              </Box>
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          marginTop: "15px",
          marginBottom: "25px",
          marginLeft: "20px",
          marginRight: "20px",
        }}
      >
        <ThemeProvider theme={tableTheme}>
          <MaterialReactTable
            columns={columns}
            data={data}
            initialState={initialState}
            positionActionsColumn="last"
            autoResetPageIndex={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableColumnActions={true}
            enableColumnFilters={false}
            enableGrouping={true}
            enableHiding={false}
            enableColumnDragging={false}
            enableRowSelection={false}
            localization={customLocalization}
            muiTableHeadCellProps={tableHeadCellProps}
            muiSearchTextFieldProps={muiSearchTextFieldProps}
            muiTableBodyCellProps={({}) => ({
              align: "left",
              sx: {
                color: "black",
                borderColor: "black",
              },
            })}
            muiTableBodyRowProps={({ table, row }) => {
              const rowIndex = table
                .getRowModel() //  Obtengo todas las filas visibles en el orden actual
                .rows.findIndex((r) => r.id === row.id); // Busca el índice real de la fila en la tabla
              return {
                sx: {
                  backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f0f0f0", // Alternar los colores correctamente, incluso si hay agrupaciones o filtrados
                },
              };
            }}
            renderTopToolbarCustomActions={() => (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="es"
                    localeText={
                      esES.components.MuiLocalizationProvider.defaultProps
                        .localeText
                    }
                  >
                    <DatePicker
                      label="Desde"
                      value={fechaDesde}
                      onChange={(newValue) => setFechaDesde(newValue)}
                      maxDate={fechaHasta || dayjs()}
                      slotProps={{
                        actionBar: { actions: ["clear"] }, // Limpiar
                        textField: {
                          size: "small",
                          sx: { width: 150 },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="es"
                    localeText={
                      esES.components.MuiLocalizationProvider.defaultProps
                        .localeText
                    }
                  >
                    <DatePicker
                      label="Hasta"
                      value={fechaHasta}
                      onChange={(newValue) => setFechaHasta(newValue)}
                      minDate={fechaDesde}
                      maxDate={dayjs()}
                      slotProps={{
                        actionBar: { actions: ["clear"] }, // Limpiar
                        textField: {
                          size: "small",
                          sx: { width: 150 },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {/* Checkbox para mostrar inactivos */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showInactivos}
                        onChange={(e) => setShowInactivos(e.target.checked)}
                      />
                    }
                    label="Mostrar Inactivos"
                  />
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    startIcon={<FilterAltIcon />}
                    onClick={obtenerDatosPatente}
                    disabled={!isFilterEnabled}
                  >
                    Filtrar
                  </Button>
                </Box>
              </>
            )}
          />
        </ThemeProvider>
      </Box>
    </>
  );
}

export default DetallesPatente;
